import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface CrisisDeclarationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CrisisDeclarationModal({ isOpen, onClose }: CrisisDeclarationModalProps) {
  const [formData, setFormData] = useState({
    incidentNumber: '',
    declarationType: '',
    reason: '',
    impactLevel: 'high',
    immediateActions: '',
    declaredBy: '',
    additionalNotes: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission logic here
    console.log('Crisis Declaration Form Submitted:', formData);
    onClose();
  };

  // Sample incident numbers
  const incidentNumbers = [
    '04-01-2024-123456',
    '04-01-2024-123457',
    '04-01-2024-123458',
    '04-01-2024-123459',
  ];

  // Authorized declarers
  const authorizedDeclarers = [
    { name: "Larry Hawk ('Sniper')", role: "LIH" },
    { name: "Lebron Jordan ('GOAT 2')", role: "EIC" },
  ];

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Crisis Declaration Form
                    </Dialog.Title>
                    <form onSubmit={handleSubmit} className="mt-4">
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="incidentNumber" className="block text-sm font-medium text-gray-700">
                            Incident Number
                          </label>
                          <select
                            id="incidentNumber"
                            name="incidentNumber"
                            value={formData.incidentNumber}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          >
                            <option value="">Select incident number...</option>
                            {incidentNumbers.map((number) => (
                              <option key={number} value={number}>{number}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="declarationType" className="block text-sm font-medium text-gray-700">
                            Declaration Type
                          </label>
                          <select
                            id="declarationType"
                            name="declarationType"
                            value={formData.declarationType}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          >
                            <option value="">Select type...</option>
                            <option value="cyber-attack">Cyber Attack</option>
                            <option value="data-breach">Data Breach</option>
                            <option value="system-outage">System Outage</option>
                            <option value="ransomware">Ransomware</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                            Reason for Declaration
                          </label>
                          <textarea
                            id="reason"
                            name="reason"
                            rows={3}
                            value={formData.reason}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="impactLevel" className="block text-sm font-medium text-gray-700">
                            Impact Level
                          </label>
                          <select
                            id="impactLevel"
                            name="impactLevel"
                            value={formData.impactLevel}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          >
                            <option value="critical">Critical</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="immediateActions" className="block text-sm font-medium text-gray-700">
                            Immediate Actions Required
                          </label>
                          <textarea
                            id="immediateActions"
                            name="immediateActions"
                            rows={3}
                            value={formData.immediateActions}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="declaredBy" className="block text-sm font-medium text-gray-700">
                            Declared By
                          </label>
                          <select
                            id="declaredBy"
                            name="declaredBy"
                            value={formData.declaredBy}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          >
                            <option value="">Select declarer...</option>
                            {authorizedDeclarers.map((declarer) => (
                              <option key={declarer.name} value={`${declarer.name} - ${declarer.role}`}>
                                {declarer.name} - {declarer.role}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700">
                            Additional Notes
                          </label>
                          <textarea
                            id="additionalNotes"
                            name="additionalNotes"
                            rows={3}
                            value={formData.additionalNotes}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                        >
                          Declare Crisis
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                          onClick={onClose}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}